// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Compras/Relatorios/FluxoCaixaCompras.tsx
//
//  Fase 3 · Iter 3.1 — Relatório de Fluxo de Caixa (Compras).
//  Projeta parcelas de ContaPagar geradas por recebimentos, com KPIs,
//  série temporal (bar chart) e drill-down em tabela.
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import {
  AlertCircle, ArrowDownCircle, ArrowUpCircle, CalendarClock, Clock,
  DollarSign, Download, TrendingDown, Wallet,
} from 'lucide-react';
import { format, subDays, addDays } from 'date-fns';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { useFluxoCaixaCompras } from '@/features/compras/hooks/useRecebimento';
import type { AgrupamentoFluxoCaixa } from '@/features/compras/types';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FornecedorCombobox } from '@/components/FornecedorCombobox';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtData = (s?: string) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); }
  catch { return s; }
};

const corSituacao = (s: string) => {
  if (s === 'Pago') return 'bg-green-500/10 text-green-700';
  if (s === 'Vence hoje') return 'bg-yellow-500/10 text-yellow-700';
  if (s.startsWith('Vencido')) return 'bg-destructive/10 text-destructive';
  return 'bg-blue-500/10 text-blue-700';
};

const FluxoCaixaCompras = () => {
  const hoje = new Date();
  const [dataInicio, setDataInicio] = useState(format(subDays(hoje, 30), 'yyyy-MM-dd'));
  const [dataFim,    setDataFim]    = useState(format(addDays(hoje, 90), 'yyyy-MM-dd'));
  // USA-COM-15: o backend já filtra por fornecedor.
  const [fornecedorId, setFornecedorId] = useState<number | null>(null);
  const [agrupamento, setAgrupamento] = useState<AgrupamentoFluxoCaixa>('Semana');
  const [apenasAbertos, setApenasAbertos] = useState(true);

  const { data, isLoading } = useFluxoCaixaCompras({
    dataInicio, dataFim, agrupamento, apenasAbertos, fornecedorId: fornecedorId ?? undefined,
  });

  const kpis = data?.resumo;
  const serie = data?.seriePorPeriodo ?? [];
  const topForn = data?.topFornecedores ?? [];
  const parcelas = data?.parcelas ?? [];

  const chartData = useMemo(
    () => serie.map((p) => ({
      periodo:   p.rotuloPeriodo,
      vencido:   p.valorVencido,
      aberto:    p.valorAberto,
      pago:      p.valorPago,
    })),
    [serie],
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Fluxo de Caixa · Compras</h1>
        <p className="text-muted-foreground mt-1">
          Projeção de pagamentos a fornecedores gerados por recebimentos de compra,
          com recortes de vencido, a vencer em 7 dias e em 30 dias.
        </p>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="h-4 w-4" /> Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <Label>Data inicial</Label>
            <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Data final</Label>
            <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          </div>
          <div className="space-y-1 w-72">
            <Label>Fornecedor</Label>
            <FornecedorCombobox value={fornecedorId} onChange={setFornecedorId}
              placeholder="Todos os fornecedores" />
          </div>
          <div className="space-y-1">
            <Label>Agrupar por</Label>
            <Select value={agrupamento} onValueChange={(v) => setAgrupamento(v as AgrupamentoFluxoCaixa)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Dia">Dia</SelectItem>
                <SelectItem value="Semana">Semana</SelectItem>
                <SelectItem value="Mes">Mês</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex items-center gap-2">
              <Switch checked={apenasAbertos} onCheckedChange={setApenasAbertos} />
              <Label className="text-sm cursor-pointer" onClick={() => setApenasAbertos((v) => !v)}>
                Apenas em aberto
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {isLoading || !kpis ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <KpiCard
            icon={<DollarSign className="h-4 w-4 text-blue-500" />}
            label="Total no período"
            valor={kpis.totalGeral}
            subtitulo={`${kpis.totalParcelas} parcela${kpis.totalParcelas === 1 ? '' : 's'}`}
          />
          <KpiCard
            icon={<TrendingDown className="h-4 w-4 text-destructive" />}
            label="Vencido"
            valor={kpis.totalVencido}
            destaque={kpis.totalVencido > 0 ? 'destructive' : undefined}
          />
          <KpiCard
            icon={<Clock className="h-4 w-4 text-yellow-500" />}
            label="A vencer (7 dias)"
            valor={kpis.totalAVencer7d}
            destaque={kpis.totalAVencer7d > 0 ? 'warning' : undefined}
          />
          <KpiCard
            icon={<ArrowUpCircle className="h-4 w-4 text-amber-500" />}
            label="A vencer (30 dias)"
            valor={kpis.totalAVencer30d}
          />
          <KpiCard
            icon={<ArrowDownCircle className="h-4 w-4 text-green-500" />}
            label="Já pago"
            valor={kpis.totalPago}
          />
        </div>
      )}

      {/* Gráfico */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Projeção por período</CardTitle>
        </CardHeader>
        <CardContent className="h-80">
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Sem dados no período selecionado.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(v: number) => fmt(v)}
                  labelFormatter={(l) => `Período: ${l}`}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="vencido" name="Vencido"  stackId="a" fill="hsl(var(--destructive))" />
                <Bar dataKey="aberto"  name="Em aberto" stackId="a" fill="#3b82f6" />
                <Bar dataKey="pago"    name="Pago"     stackId="a" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Top fornecedores */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Wallet className="h-4 w-4" /> Top fornecedores (por saldo em aberto)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : topForn.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem dados.</p>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead className="text-right">Parcelas</TableHead>
                    <TableHead className="text-right">Em aberto</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topForn.map((f) => (
                    <TableRow key={f.fornecedorId}>
                      <TableCell className="max-w-[360px] truncate" title={f.fornecedorNome ?? ''}>
                        {f.fornecedorNome ?? '(sem nome)'}
                      </TableCell>
                      <TableCell className="text-right text-xs">{f.quantidadeParcelas}</TableCell>
                      <TableCell className="text-right font-semibold">{fmt(f.valorAberto)}</TableCell>
                      <TableCell className="text-right">{fmt(f.valorTotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Parcelas (detalhe) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="h-4 w-4" /> Parcelas no período
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : parcelas.length === 0 ? (
            <div className="p-4 rounded-md bg-muted/40 text-sm text-muted-foreground flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <span>Nenhuma parcela encontrada no período. Ajuste os filtros ou cadastre recebimentos em Compras → Importar NF-e.</span>
            </div>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead>NF / Parcela</TableHead>
                    <TableHead>Receb.</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Situação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parcelas.map((p) => (
                    <TableRow key={p.contaPagarBaixaId}>
                      <TableCell className="text-sm">{fmtData(p.dtVencimento)}</TableCell>
                      <TableCell className="text-sm max-w-[260px] truncate" title={p.fornecedorNome ?? ''}>
                        {p.fornecedorNome ?? '(sem nome)'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {p.numeroNf ? `${p.numeroNf} · ` : ''}{p.nroParcela}/{p.qtdParcelasTotal}
                      </TableCell>
                      <TableCell className="text-xs font-mono">#{p.recebimentoNumero}</TableCell>
                      <TableCell className="text-right font-semibold">{fmt(p.valor)}</TableCell>
                      <TableCell>
                        <Badge className={`${corSituacao(p.situacao)} border-0`} variant="secondary">
                          {p.situacao}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FluxoCaixaCompras;

// ── KpiCard (local helper) ───────────────────────────────────────────────────

function KpiCard({
  icon, label, valor, subtitulo, destaque,
}: {
  icon:      React.ReactNode;
  label:     string;
  valor:     number;
  subtitulo?: string;
  destaque?:  'destructive' | 'warning';
}) {
  const borda =
    destaque === 'destructive' ? 'border-destructive/50'
    : destaque === 'warning'    ? 'border-yellow-500/50'
    : '';
  return (
    <Card className={borda}>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs text-muted-foreground flex items-center gap-2 font-normal">
          {icon} {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{fmt(valor)}</div>
        {subtitulo && (
          <p className="text-xs text-muted-foreground mt-1">{subtitulo}</p>
        )}
      </CardContent>
    </Card>
  );
}
